import { FunnelType } from "@/@types/Upsell.ts";
import { ColorPickerCustom as ColorPicker } from "@/components/shared/ColorPicker/ColorPicker.tsx";
import { Input } from "@/components/shared/Input/Input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import LayoutInside from "@/layout/LayoutInside";
import { OffersService } from "@/services/offers.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useUpsells } from "../hooks/useUpsell";
import FinishStep from "./FinishStep";

import { ComboBox } from "@/components/shared/ComboBox/ComboBox.tsx";
import { toast } from "@/hooks/use-toast";
import { ProductsService } from "@/services/products.service.ts";
import { normalizeUrlWhenPaste } from '@/utils/normalize';
import { z } from "zod";
import { useUpsellSchema } from "../schemas/formSchemas";
import UpsellPreview from "./UpsellPreview";

export default function CreateUpsell() {
  const navigate = useNavigate();
  const { id: productId } = useParams<{ id: string }>();
  const { t } = useTranslation(['upsells', 'common']);
  const { getStepSchema, upsellSchema } = useUpsellSchema();

  const defaultValues = {
    name: '',
    funnel_type: FunnelType.UpSell,
    page: '',
    offer_id: '',
    accepted_page: '',
    recused_page: '',
    up_sell_display_text: t('form_placeholders.default_accept'),
    up_sell_button_color: '#5CCE5E',
    up_sell_display_text_color: '#000000',
    down_sell_display_text: t('form_placeholders.default_decline'),
    down_sell_button_color: '#DC2626',
    down_sell_display_text_color: '#FFFFFF',
    product_id: '',
  };

  const { createUpsell, isCreating } = useUpsells(productId!);
  const [step, setStep] = useState(1);
  const [isComplete, setIsComplete] = useState(false);
  const [createdUpsell, setCreatedUpsell] = useState<any>(null);

  const [offerSelected, setOfferSelected] = useState({
    name: "",
    price: ""
  })

  type UpsellFormValues = z.infer<typeof upsellSchema>;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm<UpsellFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(getStepSchema(step)),
    defaultValues,
  });

  const formValues = watch();

  async function fetchProductOptions(query: string) {
    return await ProductsService.getProductsUpSellOptions(productId, query);
  }

  const selectedProductId = watch('product_id');

  const fetchOfferOptions = async (query: string) => {
    if (!selectedProductId) return [];

    return await OffersService.getUpsellsOffers({
      productId: selectedProductId,
      q: query || '',
    });
  };
  const breadcrumbItems = [
    { text: t('products'), link: '/products' },
    {
      text: t('edit_product'),
      link: `/products/edit-product/${productId}/upsells`,
    },
    {
      text: t('create_upsell'),
      link: `/products/edit-product/${productId}/upsells/create`,
    },
  ];

  const nextStep = async () => {
    const isStepValid = await trigger();
    if (isStepValid) {
      setStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (step === 4) {
      reset({
        ...formValues,
        up_sell_display_text: defaultValues.up_sell_display_text,
        up_sell_button_color: defaultValues.up_sell_button_color,
        up_sell_display_text_color: defaultValues.up_sell_display_text_color,
        down_sell_display_text: defaultValues.down_sell_display_text,
        down_sell_button_color: defaultValues.down_sell_button_color,
        down_sell_display_text_color:
          defaultValues.down_sell_display_text_color,
      });
    }
    setStep((prev) => prev - 1);
  };

  const onSubmit = async (data: UpsellFormValues) => {
    if (step < 4) {
      await nextStep();
      return;
    }

    try {
      const formData = {
        ...formValues,
        accepted_page: formValues.accepted_page || undefined,
        recused_page: formValues.recused_page || undefined,
      };

      const response = await createUpsell({
        productId: productId!,
        data: formData,
      });

      setCreatedUpsell(response);
      setIsComplete(true);
    } catch (error) {
      toast({
        variant: 'error',
        description:
          error?.response.data.message || t('common:data_submission_error'),
      });
    }
  };

  const renderStep = () => {
    if (isComplete) {
      return (
        <FinishStep
          upsell={createdUpsell?.data}
          onBack={() => navigate(`/products/edit-product/${productId}/upsells`)}
        />
      );
    }

    switch (step) {
      case 1:
        return (
          <div className='space-y-6'>
            <div>
              <Input
                label={t('form.name')}
                helperText={errors.name?.message}
                {...register('name')}
                placeholder={t('form.name_placeholder')}
              />
            </div>

            <div>
              <Label>{t('form.type')}</Label>
              <Select
                onValueChange={(value) =>
                  setValue('funnel_type', value as FunnelType)
                }
                defaultValue={formValues.funnel_type}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('form.type_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FunnelType.UpSell}>
                    {t('form.type_upsell')}
                  </SelectItem>
                  <SelectItem value={FunnelType.DownSell}>
                    {t('form.type_downsell')}
                  </SelectItem>
                </SelectContent>
              </Select>
              {errors.funnel_type && (
                <p className='text-sm text-red-500 mt-1'>
                  {errors.funnel_type.message}
                </p>
              )}
            </div>

            <div>
              <Input
                label={t('form.page')}
                helperText={errors.page?.message}
                {...register('page')}
                placeholder={t('form.page_placeholder')}
                onFocus={(e) => {
                  if (!e.target.value) {
                    e.target.value = 'https://';
                    setValue('page', 'https://');
                  }
                }}
                onPaste={(e) => {
                  const finalValue = normalizeUrlWhenPaste(e);
                  setValue('page', finalValue);
                }}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className='space-y-6'>
            <ComboBox
              fetchOptions={fetchProductOptions}
              value={formValues.product_id}
              onChange={(value) => {
                clearErrors(['product_id', 'offer_id']);
                setValue('product_id', value.value);
                setValue('offer_id', '');
                setOfferSelected({ name: value.label, price: "" })
              }}
              returnFullObject
              label={t('form.product')}
              placeholder={t('form.product_placeholder')}
              helperText={errors.product_id?.message}
            />
            <ComboBox
              key={formValues.product_id}
              fetchOptions={fetchOfferOptions}
              value={formValues.offer_id}
              onChange={(value) => {
                clearErrors('offer_id');
                setValue('offer_id', value.value);
                setOfferSelected({ name: offerSelected.name, price: value.price })
              }}
              returnFullObject
              label={t('form.offer')}
              placeholder={t('form.offer_placeholder')}
              helperText={errors.offer_id?.message}
              queryKeyExtra={[formValues.product_id]}
              disabled={!selectedProductId}
            />
          </div>
        );

      case 3:
        return (
          <div className='space-y-6 w-full max-w-lg mx-auto'>
            <div className='text-left'>
              <Input
                label={t('form.accepted_page')}
                helperText={errors.accepted_page?.message}
                {...register('accepted_page')}
                placeholder='https://exemplo.com/proximo-upsell'
                onFocus={(e) => {
                  if (!e.target.value) {
                    e.target.value = 'https://';
                    setValue('accepted_page', 'https://');
                  }
                }}
                onPaste={(e) => {
                  const finalValue = normalizeUrlWhenPaste(e);
                  setValue('accepted_page', finalValue);
                }}
              />
            </div>

            <div className='text-left'>
              <Input
                label={t('form.recused_page')}
                helperText={errors.recused_page?.message}
                {...register('recused_page')}
                placeholder='https://exemplo.com/downsell'
                onFocus={(e) => {
                  if (!e.target.value) {
                    e.target.value = 'https://';
                    setValue('recused_page', 'https://');
                  }
                }}
                onPaste={(e) => {
                  const finalValue = normalizeUrlWhenPaste(e);
                  setValue('recused_page', finalValue);
                }}
              />
            </div>

            <div className='bg-[#18181B] p-4 rounded-md flex items-center'>
              <div className='flex gap-3'>
                <Info size={20} className='text-white flex-none' />
                <span className='text-muted-foreground text-sm leading-relaxed'>
                  {t('form.upsell_description')}
                </span>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className='space-y-6'>
            <div className='space-y-6'>
              <Input
                label={t('form.up_sell_display_text')}
                helperText={errors.up_sell_display_text?.message}
                {...register('up_sell_display_text')}
                placeholder={t('form.upsell_accept_placeholder')}
              />

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <ColorPicker
                    defaultColor={formValues.up_sell_button_color}
                    onValueChange={(value) =>
                      setValue('up_sell_button_color', value)
                    }
                    label={t('form.color_bg')}
                  />
                  {errors.up_sell_button_color && (
                    <p className='text-sm text-red-500 mt-1'>
                      {errors.up_sell_button_color.message}
                    </p>
                  )}
                </div>
                <div>
                  <ColorPicker
                    defaultColor={formValues.up_sell_display_text_color}
                    onValueChange={(value) =>
                      setValue('up_sell_display_text_color', value)
                    }
                    label={t('form.color_text')}
                  />
                  {errors.up_sell_display_text_color && (
                    <p className='text-sm text-red-500 mt-1'>
                      {errors.up_sell_display_text_color.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className='space-y-6'>
              <Input
                label={t('form.down_sell_display_text')}
                helperText={errors.down_sell_display_text?.message}
                {...register('down_sell_display_text')}
                placeholder={t('form.upsell_decline_placeholder')}
              />

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <ColorPicker
                    defaultColor={formValues.down_sell_button_color}
                    onValueChange={(value) =>
                      setValue('down_sell_button_color', value)
                    }
                    label={t('form.color_bg')}
                  />
                  {errors.down_sell_button_color && (
                    <p className='text-sm text-red-500 mt-1'>
                      {errors.down_sell_button_color.message}
                    </p>
                  )}
                </div>
                <div>
                  <ColorPicker
                    defaultColor={formValues.down_sell_display_text_color}
                    onValueChange={(value) =>
                      setValue('down_sell_display_text_color', value)
                    }
                    label={t('form.color_text')}
                  />
                  {errors.down_sell_display_text_color && (
                    <p className='text-sm text-red-500 mt-1'>
                      {errors.down_sell_display_text_color.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <UpsellPreview
              formValues={{
                up_sell_display_text: formValues.up_sell_display_text,
                up_sell_button_color: formValues.up_sell_button_color,
                up_sell_display_text_color: formValues.up_sell_display_text_color,
                down_sell_display_text: formValues.down_sell_display_text,
                down_sell_button_color: formValues.down_sell_button_color,
                down_sell_display_text_color: formValues.down_sell_display_text_color,
                name: offerSelected.name,
                price: offerSelected.price,
              }}
            />
          </div>
        );
    }
  };

  return (
    <LayoutInside items={breadcrumbItems}>
      <section className='md:px-10 xl:px-0 min-h-[calc(100vh-140px)] flex items-center justify-center max-sm:pb-16'>
        <div className='max-w-lg w-full mx-auto'>
          {!isComplete && (
            <div className='text-center mb-6 max-w-xs w-full m-auto'>
              <Badge variant='secondary' className='mb-4'>
                {step}/4
              </Badge>
              <h1 className='text-2xl font-semibold'>
                {step === 1 && (
                  <>
                    {t('step1')}{' '}
                    <span className='text-primary'>{t('step1_green')}</span>
                  </>
                )}
                {step === 2 && (
                  <>
                    {t('step2_first')}{' '}
                    <span className='text-primary'>{t('step2_green')}</span>{' '}
                    {t('step2_second')}{' '}
                    <span className='text-primary'>
                      {t('step2_gree_second')}
                    </span>{' '}
                    {t('step2_third')}
                  </>
                )}
                {step === 3 && (
                  <>
                    {t('step3_first')}{' '}
                    <span className='text-primary'>{t('step3_green')}</span>{' '}
                    {t('step3_second')}
                  </>
                )}
                {step === 4 && t('step4')}
              </h1>
            </div>
          )}

          <form className='space-y-8' onSubmit={handleSubmit(onSubmit)}>
            {renderStep()}
            {!isComplete && (
              <div className='flex justify-between mt-8'>
                {step > 1 && (
                  <Button type='button' variant='ghost' onClick={prevStep}>
                    {t('back')}
                  </Button>
                )}
                <div className='flex-1' />
                <Button type='submit' disabled={isCreating} variant='default'>
                  {step === 4 ? t('finish') : t('advance')}
                </Button>
              </div>
            )}
          </form>
        </div>
      </section>
    </LayoutInside>
  );
}
